import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';

export interface ExtensionVersion {
  version: string;
  releaseNotes: string;
  uploadDate: string;
  downloadUrl: string;
}

class GlossaryExtensionService {
  private firestore = getFirestore();
  private extensionDocRef = doc(this.firestore, 'glossary', 'extension');

  /**
   * Get the current extension version data from Firestore
   * @returns Promise<ExtensionVersion | null> The extension version data or null if not found
   */
  async getCurrentVersion(): Promise<ExtensionVersion | null> {
    try {
      const docSnap = await getDoc(this.extensionDocRef);
      
      if (docSnap.exists()) {
        return docSnap.data() as ExtensionVersion;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error getting current extension version:', error);
      throw error;
    }
  }

  /**
   * Upload a new extension file and save its metadata using a Netlify function
   * @param file The extension file to upload
   * @param releaseNotes Release notes for this version
   * @param currentVersion The current version data (for version incrementing)
   * @returns Promise<ExtensionVersion> The new extension version data
   */
  async uploadExtension(
    file: File,
    releaseNotes: string,
    currentVersion: ExtensionVersion | null
  ): Promise<ExtensionVersion> {
    try {
      // Create a FormData object to send the file and metadata
      const formData = new FormData();
      formData.append('file', file);
      formData.append('releaseNotes', releaseNotes);
      
      // Upload the file using the Netlify function
      const response = await fetch('/.netlify/functions/uploadExtension', {
        method: 'POST',
        body: formData
      });
      
      // Get the response text first
      const responseText = await response.text();
      
      // Try to parse it as JSON
      let responseData: ExtensionVersion;
      try {
        const jsonData = JSON.parse(responseText);
        
        // Check if the response contains an error
        if (!response.ok) {
          throw new Error(jsonData.error || jsonData.message || 'Failed to upload extension');
        }
        
        responseData = jsonData as ExtensionVersion;
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        console.error('Response text:', responseText);
        throw new Error(`Server error: ${responseText.substring(0, 100)}...`);
      }
      
      // Save metadata to Firestore (in case the function didn't do it)
      await setDoc(this.extensionDocRef, responseData);
      
      return responseData;
    } catch (error) {
      console.error('Error uploading extension:', error);
      throw error;
    }
  }

  /**
   * Validate if a file is a valid Chrome extension package
   * @param file The file to validate
   * @returns boolean True if valid, false otherwise
   */
  isValidExtensionFile(file: File): boolean {
    return file.name.endsWith('.zip');
  }
}

export const glossaryExtensionService = new GlossaryExtensionService();